import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, ChevronLeft, ChevronRight, Eraser, FilePlus2, ImagePlus, Loader2, MousePointer2, PenLine, Plus, Printer, Redo2, Save, Trash2, Undo2, X, ZoomIn, ZoomOut } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export const INKPAD_PAGE_SIZE = { width: 794, height: 1123 };
export const INKPAD_PEN_COLORS = Object.freeze([
  { name: 'Black', value: '#111827' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Purple', value: '#7c3aed' },
]);

export function pointToSegmentDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

export function eraseIntersectingStrokes(strokes, point, radius) {
  return strokes.flatMap((stroke) => {
    if (stroke.tool === 'image') return [stroke];
    const points = stroke.points || [];
    const effectiveRadius = radius + Number(stroke.width || 1) / 2;
    if (points.length === 1) return Math.hypot(point.x - points[0].x, point.y - points[0].y) > effectiveRadius ? [stroke] : [];
    if (!points.slice(1).some((next, index) => pointToSegmentDistance(point, points[index], next) <= effectiveRadius)) return [stroke];
    const sampled = [points[0]];
    points.slice(1).forEach((end, index) => {
      const start = points[index];
      const distance = Math.hypot(end.x - start.x, end.y - start.y);
      const steps = Math.max(1, Math.ceil(distance / Math.max(2, effectiveRadius / 3)));
      for (let step = 1; step <= steps; step += 1) {
        const ratio = step / steps;
        sampled.push({ ...end, x: start.x + (end.x - start.x) * ratio, y: start.y + (end.y - start.y) * ratio });
      }
    });
    const survivors = [];
    let segment = [];
    sampled.forEach((candidate) => {
      if (Math.hypot(point.x - candidate.x, point.y - candidate.y) > effectiveRadius) segment.push(candidate);
      else if (segment.length) { survivors.push(segment); segment = []; }
    });
    if (segment.length) survivors.push(segment);
    return survivors.map((survivor, index) => ({ ...stroke, id: index === 0 ? stroke.id : `${stroke.id}-split-${Date.now()}-${index}`, points: survivor }));
  });
}

export function formatInkpadPrice(price) {
  if (!price) return '';
  const exponent = { BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3, BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0 }[price.currencyCode] ?? 2;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: price.currencyCode || 'INR', minimumFractionDigits: exponent, maximumFractionDigits: exponent }).format(Number(price.amountMinor || 0) / (10 ** exponent));
}

function newPage(pageNumber = 1) {
  return { pageNumber, ...INKPAD_PAGE_SIZE, backgroundType: 'blank', strokes: [] };
}

const isImageItem = (item) => item?.tool === 'image';

function cloneStrokes(strokes) {
  return JSON.parse(JSON.stringify(strokes || []));
}

function InkpadButton({ active = false, disabled = false, onClick, children, title }) {
  return <button type="button" title={title} disabled={disabled} onClick={onClick} className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition ${active ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'} disabled:cursor-not-allowed disabled:opacity-45`}>{children}</button>;
}

export default function DektalInkpadWorkspace({ patient, prescription, appointmentId, encounterId, onClose }) {
  const canvasRef = useRef(null);
  const imageInputRef = useRef(null);
  const imageGestureRef = useRef(null);
  const drawingRef = useRef(false);
  const strokeIdRef = useRef(null);
  const undoRef = useRef({});
  const redoRef = useRef({});
  const idempotencyKeyRef = useRef(globalThis.crypto?.randomUUID?.() || `inkpad-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [inkpadDocument, setInkpadDocument] = useState(null);
  const [pages, setPages] = useState([newPage()]);
  const [pageIndex, setPageIndex] = useState(0);
  const [tool, setTool] = useState('pen');
  const [penWidth, setPenWidth] = useState(3);
  const [penColor, setPenColor] = useState(INKPAD_PEN_COLORS[0].value);
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [treatmentCatalog, setTreatmentCatalog] = useState([]);
  const [performedTreatmentId, setPerformedTreatmentId] = useState('');
  const [performedToothSite, setPerformedToothSite] = useState('');
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [lastFollowUp, setLastFollowUp] = useState(null);

  const currentPage = pages[pageIndex] || pages[0];
  const editable = inkpadDocument ? Boolean(workspace?.access?.canEdit) : Boolean(workspace?.access?.canCreate);
  const price = workspace?.access?.prices?.find((entry) => entry.currencyCode === 'INR') || workspace?.access?.prices?.[0] || null;

  const load = useCallback(async () => {
    if (!patient?.id || !prescription?.id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get('/dektal-inkpad/workspace', { params: { patientId: patient.id, prescriptionId: prescription.id, appointmentId: appointmentId || undefined, encounterId: encounterId || undefined } });
      setWorkspace(data);
      setInkpadDocument(data.document || null);
      const loadedPages = data.pages?.length ? data.pages : data.document?.Pages;
      setPages(loadedPages?.length ? loadedPages.map((page, index) => ({ ...page, pageNumber: index + 1 })) : [newPage()]);
      setPageIndex(0);
      setDirty(false);
      undoRef.current = {};
      redoRef.current = {};
    } catch (error) {
      const status = Number(error.response?.status || 0);
      const backendMessage = error.response?.data?.error;
      const errorState = status === 401 || status === 403
        ? { kind: 'unauthorized', title: 'InkPad access denied', message: backendMessage || 'You do not have clinical access to Dektal InkPad.', retryable: false }
        : status === 404
          ? { kind: 'not_found', title: 'InkPad context not found', message: backendMessage || 'The patient, prescription, appointment, or encounter could not be found.', retryable: false }
          : status >= 500 || status === 0
            ? { kind: 'server', title: 'InkPad could not be loaded', message: backendMessage || 'Unable to open Dektal InkPad', retryable: true }
            : { kind: 'request', title: 'InkPad could not be loaded', message: backendMessage || 'Unable to open Dektal InkPad', retryable: true };
      setWorkspace(null);
      setLoadError(errorState);
      toast.error(errorState.message);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, encounterId, patient?.id, prescription?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!workspace?.clinic?.inkpadBackground?.exists) { setBackgroundUrl(''); return undefined; }
    let active = true; let objectUrl = '';
    api.get('/dektal-inkpad/background/content', { responseType: 'blob' }).then(({ data }) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(data); setBackgroundUrl(objectUrl);
    }).catch(() => setBackgroundUrl(''));
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [workspace?.clinic?.inkpadBackground?.exists, workspace?.clinic?.inkpadBackground?.updatedAt]);

  useEffect(() => {
    const ids = [...new Set(pages.flatMap((page) => (page.strokes || []).filter(isImageItem).map((item) => Number(item.attachmentId))))].filter(Boolean);
    const missing = ids.filter((id) => !imageUrls[id]);
    if (!missing.length) return undefined;
    let active = true;
    Promise.all(missing.map(async (id) => {
      const { data } = await api.get(`/upload/${id}/content`, { responseType: 'blob' });
      return [id, URL.createObjectURL(data)];
    })).then((entries) => { if (active) setImageUrls((current) => ({ ...current, ...Object.fromEntries(entries) })); else entries.forEach(([, url]) => URL.revokeObjectURL(url)); }).catch(() => toast.error('One or more InkPad images could not be loaded.'));
    return () => { active = false; };
  }, [pages, imageUrls]);

  useEffect(() => {
    api.get('/prescriptions/treatment-catalog').then(({ data }) => setTreatmentCatalog(Array.isArray(data) ? data : [])).catch(() => setTreatmentCatalog([]));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !currentPage) return;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    (currentPage.strokes || []).filter((stroke) => !isImageItem(stroke)).forEach((stroke) => {
      const points = stroke.points || [];
      if (!points.length) return;
      context.beginPath();
      context.strokeStyle = stroke.color || '#111827';
      context.lineWidth = Number(stroke.width || 2);
      context.moveTo(points[0].x, points[0].y);
      if (points.length === 1) context.lineTo(points[0].x + 0.01, points[0].y + 0.01);
      points.slice(1).forEach((point) => context.lineTo(point.x, point.y));
      context.stroke();
    });
  }, [currentPage]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: Math.max(0, Math.min(canvas.width, (event.clientX - rect.left) * canvas.width / rect.width)), y: Math.max(0, Math.min(canvas.height, (event.clientY - rect.top) * canvas.height / rect.height)), pressure: Number.isFinite(event.pressure) ? event.pressure : 0.5 };
  };

  const mutateCurrentStrokes = (mutator) => {
    setPages((existing) => existing.map((page, index) => index === pageIndex ? { ...page, strokes: mutator(page.strokes || []) } : page));
    setDirty(true);
  };

  const snapshotForUndo = () => {
    undoRef.current[pageIndex] = [...(undoRef.current[pageIndex] || []), cloneStrokes(currentPage.strokes)].slice(-60);
    redoRef.current[pageIndex] = [];
  };

  const handlePointerDown = (event) => {
    if (!editable || event.button > 0) return;
    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    snapshotForUndo();
    drawingRef.current = true;
    setDrawing(true);
    const point = pointFromEvent(event);
    if (tool === 'eraser') {
      mutateCurrentStrokes((strokes) => eraseIntersectingStrokes(strokes, point, Math.max(8, penWidth * 2.5)));
      return;
    }
    const id = globalThis.crypto?.randomUUID?.() || `stroke-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    strokeIdRef.current = id;
    const imageUnderPoint = [...(currentPage.strokes || [])].reverse().find((item) => isImageItem(item) && point.x >= item.x && point.x <= item.x + item.width && point.y >= item.y && point.y <= item.y + item.height);
    mutateCurrentStrokes((strokes) => [...strokes, { id, tool: 'pen', color: penColor, width: penWidth, points: [point], ...(imageUnderPoint ? { annotationForImageId: imageUnderPoint.id } : {}) }]);
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current || !editable) return;
    event.preventDefault();
    const point = pointFromEvent(event);
    if (tool === 'eraser') {
      mutateCurrentStrokes((strokes) => eraseIntersectingStrokes(strokes, point, Math.max(8, penWidth * 2.5)));
      return;
    }
    mutateCurrentStrokes((strokes) => strokes.map((stroke) => stroke.id === strokeIdRef.current ? { ...stroke, points: [...stroke.points, point] } : stroke));
  };

  const endDrawing = (event) => {
    if (!drawingRef.current) return;
    event?.preventDefault?.();
    drawingRef.current = false;
    strokeIdRef.current = null;
    setDrawing(false);
  };

  const undo = () => {
    const stack = undoRef.current[pageIndex] || [];
    if (!stack.length || !editable) return;
    const previous = stack[stack.length - 1];
    undoRef.current[pageIndex] = stack.slice(0, -1);
    redoRef.current[pageIndex] = [...(redoRef.current[pageIndex] || []), cloneStrokes(currentPage.strokes)];
    mutateCurrentStrokes(() => previous);
  };

  const redo = () => {
    const stack = redoRef.current[pageIndex] || [];
    if (!stack.length || !editable) return;
    const next = stack[stack.length - 1];
    redoRef.current[pageIndex] = stack.slice(0, -1);
    undoRef.current[pageIndex] = [...(undoRef.current[pageIndex] || []), cloneStrokes(currentPage.strokes)];
    mutateCurrentStrokes(() => next);
  };

  const clearPage = () => {
    if (!editable || !(currentPage.strokes || []).some((item) => !isImageItem(item)) || !window.confirm(`Clear all handwriting from page ${pageIndex + 1}? Images will remain.`)) return;
    snapshotForUndo();
    mutateCurrentStrokes((items) => items.filter(isImageItem));
  };

  const addPage = () => {
    if (!editable) return;
    setPages((existing) => [...existing, newPage(existing.length + 1)]);
    setPageIndex(pages.length);
    setDirty(true);
  };

  const changeBackground = (backgroundType) => {
    if (!editable) return;
    if (backgroundType === 'clinic' && !workspace?.clinic?.inkpadBackground?.exists) { toast.error('No clinic background uploaded'); return; }
    setPages((existing) => existing.map((page, index) => index === pageIndex ? { ...page, backgroundType } : page));
    setDirty(true);
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editable) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { toast.error('InkPad images must be JPG or PNG files'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('InkPad image must be 5 MB or smaller'); return; }
    setUploadingImage(true);
    try {
      const body = new FormData();
      body.append('prescriptionId', prescription.id); body.append('purpose', 'inkpad'); body.append('files', file);
      const { data } = await api.post('/upload', body);
      const attachment = Array.isArray(data) ? data[0] : null;
      if (!attachment?.id) throw new Error('Image upload did not return an attachment');
      const ratio = file.type === 'image/png' || file.type === 'image/jpeg' ? 0.72 : 0.72;
      const image = { id: `image-${attachment.id}-${Date.now()}`, tool: 'image', attachmentId: Number(attachment.id), x: 147, y: 280, width: 500, height: Math.round(500 * ratio), scale: 1 };
      snapshotForUndo(); mutateCurrentStrokes((items) => [...items, image]); setSelectedImageId(image.id); setTool('select');
    } catch (error) { toast.error(error.response?.data?.error || error.message || 'Unable to add image'); }
    finally { setUploadingImage(false); }
  };

  const updateImage = (id, changes) => mutateCurrentStrokes((items) => {
    const previous = items.find((item) => item.id === id && isImageItem(item));
    if (!previous) return items;
    const next = { ...previous, ...changes };
    const scaleX = next.width / previous.width; const scaleY = next.height / previous.height;
    return items.map((item) => {
      if (item.id === id) return next;
      if (item.annotationForImageId !== id || !Array.isArray(item.points)) return item;
      return { ...item, points: item.points.map((point) => ({ ...point, x: next.x + (point.x - previous.x) * scaleX, y: next.y + (point.y - previous.y) * scaleY })) };
    });
  });
  const beginImageGesture = (event, image, mode) => {
    if (!editable) return;
    event.preventDefault(); event.stopPropagation(); snapshotForUndo(); setSelectedImageId(image.id);
    imageGestureRef.current = { pointerId: event.pointerId, mode, startX: event.clientX, startY: event.clientY, image: { ...image }, rect: canvasRef.current.getBoundingClientRect() };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const moveImageGesture = (event) => {
    const gesture = imageGestureRef.current; if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault(); event.stopPropagation();
    const dx = (event.clientX - gesture.startX) * INKPAD_PAGE_SIZE.width / gesture.rect.width;
    const dy = (event.clientY - gesture.startY) * INKPAD_PAGE_SIZE.height / gesture.rect.height;
    if (gesture.mode === 'move') updateImage(gesture.image.id, { x: Math.max(0, Math.min(INKPAD_PAGE_SIZE.width - gesture.image.width, gesture.image.x + dx)), y: Math.max(0, Math.min(INKPAD_PAGE_SIZE.height - gesture.image.height, gesture.image.y + dy)) });
    else {
      const nextWidth = Math.max(60, Math.min(INKPAD_PAGE_SIZE.width - gesture.image.x, gesture.image.width + dx));
      const ratio = gesture.image.height / gesture.image.width;
      updateImage(gesture.image.id, { width: nextWidth, height: Math.min(INKPAD_PAGE_SIZE.height - gesture.image.y, nextWidth * ratio), scale: nextWidth / gesture.image.width * Number(gesture.image.scale || 1) });
    }
  };
  const endImageGesture = (event) => { if (imageGestureRef.current?.pointerId === event.pointerId) imageGestureRef.current = null; };
  const selectedImage = (currentPage?.strokes || []).find((item) => item.id === selectedImageId && isImageItem(item));
  const zoomSelectedImage = (factor) => {
    if (!selectedImage) return; snapshotForUndo();
    const nextWidth = Math.max(60, Math.min(INKPAD_PAGE_SIZE.width - selectedImage.x, selectedImage.width * factor));
    const ratio = selectedImage.height / selectedImage.width;
    updateImage(selectedImage.id, { width: nextWidth, height: Math.min(INKPAD_PAGE_SIZE.height - selectedImage.y, nextWidth * ratio), scale: Number(selectedImage.scale || 1) * factor });
  };
  const removeSelectedImage = () => { if (!selectedImage) return; snapshotForUndo(); mutateCurrentStrokes((items) => items.filter((item) => item.id !== selectedImage.id)); setSelectedImageId(null); };

  const addPerformedTreatment = async () => {
    const selected = treatmentCatalog.find((item) => String(item.id) === String(performedTreatmentId));
    if (!selected || !editable) return;
    setSavingTreatment(true);
    try {
      const created = await api.post('/prescriptions/treatment', { prescriptionId: prescription.id, patientId: patient.id, appointmentId: appointmentId ? Number(appointmentId) : null, encounterId: encounterId ? Number(encounterId) : null, treatmentId: selected.id, sourceType: selected.sourceType || 'master', customTreatmentId: selected.customTreatmentId || null, toothNumber: performedToothSite.trim(), estimatedFee: selected.sellingPrice ?? selected.price ?? null });
      const started = await api.put(`/prescriptions/treatment/${created.data.id}`, { status: 'ongoing', toothNumber: performedToothSite.trim() });
      await api.put(`/prescriptions/treatment/${started.data.id}`, { status: 'completed', toothNumber: performedToothSite.trim() });
      if (encounterId) await api.post(`/encounters/${encounterId}/send-to-billing`);
      setPerformedTreatmentId(''); setPerformedToothSite(''); toast.success('Performed treatment added to billing, inventory and patient history workflow.');
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to add performed treatment'); }
    finally { setSavingTreatment(false); }
  };

  const scheduleFollowUp = async () => {
    if (!followUpDate || !followUpTime) { toast.error('Choose the next visit date and time'); return; }
    setSavingFollowUp(true);
    try {
      const { data } = await api.post('/follow-ups', { patientId: patient.id, prescriptionId: prescription.id, dueDate: followUpDate, time: followUpTime, doctorAssigned: workspace?.doctor?.name || workspace?.encounter?.assignedDoctorNameSnapshot || null, whatsappReminderEnabled: true });
      setLastFollowUp(data); toast.success(data?.existing ? 'This next visit is already scheduled.' : 'Next visit scheduled and patient link prepared.');
    } catch (error) { toast.error(error.response?.data?.error || 'Unable to schedule next visit'); }
    finally { setSavingFollowUp(false); }
  };

  const save = async () => {
    if (!editable) return;
    setSaving(true);
    try {
      const { data } = await api.put('/dektal-inkpad/workspace', { patientId: patient.id, prescriptionId: prescription.id, appointmentId: appointmentId ? Number(appointmentId) : null, encounterId: encounterId ? Number(encounterId) : null, version: inkpadDocument?.version || null, pages: pages.map((page, index) => ({ ...page, pageNumber: index + 1 })) }, { headers: { 'Idempotency-Key': idempotencyKeyRef.current } });
      setInkpadDocument(data.document);
      setPages(data.document.Pages || pages);
      setWorkspace((value) => ({ ...value, document: data.document, access: { ...value.access, canViewHistorical: true } }));
      setDirty(false);
      toast.success(data.created ? 'InkPad prescription saved. One InkPad use recorded.' : 'InkPad prescription saved.');
      const refreshed = await api.get('/prescriptions/inkpad/access', { params: { prescriptionId: prescription.id } });
      setWorkspace((value) => ({ ...value, access: refreshed.data }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to save InkPad prescription');
    } finally {
      setSaving(false);
    }
  };

  const printPdf = async () => {
    if (!inkpadDocument?.id || dirty) { toast.error(dirty ? 'Save the InkPad changes before creating the PDF.' : 'Save this InkPad prescription before creating the PDF.'); return; }
    const printWindow = window.open('', '_blank');
    setPrinting(true);
    try {
      const response = await api.get(`/dektal-inkpad/documents/${inkpadDocument.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (printWindow) printWindow.location.href = url;
      else window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      printWindow?.close();
      toast.error(error.response?.data?.error || 'Unable to create InkPad PDF');
    } finally {
      setPrinting(false);
    }
  };

  const quotaLabel = useMemo(() => {
    if (!workspace?.access) return '';
    if (workspace.access.unlimited) return workspace.access.salesDemo ? 'Demo access · Unlimited InkPad prescriptions' : 'Unlimited InkPad access';
    return `${workspace.access.freeUsesConsumed || 0} of ${workspace.access.freeAllowance || 0} free prescriptions used`;
  }, [workspace?.access]);
  const access = workspace?.access || null;
  const quotaExhausted = Boolean(
    access
    && !access.canCreate
    && Number(access.freeUsesRemaining || 0) === 0
    && Number(access.extraUsesRemaining || 0) === 0
    && !access.paidEntitlementActive
    && !access.unlimited
    && !access.salesDemo
  );

  return <div className="fixed inset-0 z-[80] flex flex-col bg-slate-950/65 p-0 backdrop-blur-sm sm:p-4" role="dialog" aria-modal="true" aria-label="Dektal InkPad">
    <div className="mx-auto flex min-h-0 w-full max-w-[1500px] flex-1 flex-col overflow-hidden bg-slate-100 shadow-2xl sm:rounded-3xl">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        <div><div className="flex items-center gap-2"><PenLine className="h-5 w-5 text-indigo-600"/><h2 className="text-xl font-bold text-slate-950">Dektal InkPad</h2></div><p className="mt-1 text-xs text-slate-500">{patient?.name || 'Patient'} · ID {patient?.patientId || '—'} · {workspace?.doctor?.name || workspace?.encounter?.assignedDoctorNameSnapshot || workspace?.appointment?.doctorAssigned || prescription?.treatingDoctorName || 'Treating doctor'} · {new Date().toLocaleDateString('en-IN')}</p></div>
        <div className="flex items-center gap-3"><div className="hidden text-right sm:block"><p className="text-xs font-semibold text-slate-700">{quotaLabel}</p>{quotaExhausted && price ? <p className="text-[11px] text-slate-500">{formatInkpadPrice(price)}/month</p> : null}</div><button type="button" onClick={onClose} aria-label="Close Dektal InkPad" className="rounded-full border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><X className="h-5 w-5"/></button></div>
      </header>
      {loading ? <div className="flex flex-1 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-indigo-600"/></div> : loadError ? <div className="flex flex-1 items-center justify-center p-6"><div className="max-w-lg rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm"><h3 className="text-lg font-bold text-slate-900">{loadError.title}</h3><p className="mt-2 text-sm text-slate-600">{loadError.message}</p>{loadError.retryable ? <button type="button" onClick={load} className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Retry</button> : null}</div></div> : <>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-5">
          <InkpadButton active={tool === 'pen'} disabled={!editable} onClick={() => setTool('pen')}><PenLine className="h-4 w-4"/>Pen</InkpadButton>
          <InkpadButton active={tool === 'eraser'} disabled={!editable} onClick={() => setTool('eraser')}><Eraser className="h-4 w-4"/>Eraser</InkpadButton>
          <InkpadButton active={tool === 'select'} disabled={!editable} onClick={() => setTool('select')}><MousePointer2 className="h-4 w-4"/>Select</InkpadButton>
          <InkpadButton disabled={!editable || !(undoRef.current[pageIndex] || []).length} onClick={undo}><Undo2 className="h-4 w-4"/>Undo</InkpadButton>
          <InkpadButton disabled={!editable || !(redoRef.current[pageIndex] || []).length} onClick={redo}><Redo2 className="h-4 w-4"/>Redo</InkpadButton>
          <label className="flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">Width<input aria-label="Pen thickness" type="range" min="1" max="16" step="1" value={penWidth} disabled={!editable} onChange={(event) => setPenWidth(Number(event.target.value))}/><span className="w-5 text-right">{penWidth}</span></label>
          <div className="flex min-h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2" role="group" aria-label="Pen color">
            {INKPAD_PEN_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                title={color.name}
                aria-label={`${color.name} ink`}
                aria-pressed={penColor === color.value}
                disabled={!editable}
                onClick={() => { setPenColor(color.value); setTool('pen'); }}
                className={`h-6 w-6 rounded-full border-2 transition focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-1 ${penColor === color.value ? 'scale-110 border-slate-900 shadow-sm' : 'border-white ring-1 ring-slate-300'} disabled:cursor-not-allowed disabled:opacity-45`}
                style={{ backgroundColor: color.value }}
              >
                <span className="sr-only">{color.name}</span>
              </button>
            ))}
          </div>
          <InkpadButton disabled={!editable} onClick={clearPage}><Trash2 className="h-4 w-4"/>Clear</InkpadButton>
          <InkpadButton disabled={!editable} onClick={addPage}><FilePlus2 className="h-4 w-4"/>Add Page</InkpadButton>
          <input ref={imageInputRef} type="file" accept="image/*,.jpg,.jpeg,.png" capture="environment" onChange={uploadImage} className="sr-only" aria-label="Choose InkPad image" />
          <InkpadButton disabled={!editable || uploadingImage} onClick={() => imageInputRef.current?.click()}><ImagePlus className="h-4 w-4"/>{uploadingImage ? 'Uploading…' : 'Add Image'}</InkpadButton>
          {selectedImage ? <><InkpadButton title="Zoom image out" onClick={() => zoomSelectedImage(0.9)}><ZoomOut className="h-4 w-4"/></InkpadButton><InkpadButton title="Zoom image in" onClick={() => zoomSelectedImage(1.1)}><ZoomIn className="h-4 w-4"/></InkpadButton><InkpadButton title="Remove selected image" onClick={removeSelectedImage}><Trash2 className="h-4 w-4"/>Image</InkpadButton></> : null}
          <select aria-label="Page background" value={currentPage?.backgroundType || 'blank'} disabled={!editable} onChange={(event) => changeBackground(event.target.value)} className="min-h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"><option value="blank">Blank Page</option><option value="clinic" disabled={!workspace?.clinic?.inkpadBackground?.exists}>Clinic Background</option></select>
          {!workspace?.clinic?.inkpadBackground?.exists ? <span className="text-[11px] font-medium text-amber-700">No clinic background uploaded</span> : null}
          <div className="ml-auto flex gap-2"><InkpadButton disabled={!editable || saving} onClick={save}>{saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}Save</InkpadButton><InkpadButton disabled={!inkpadDocument?.id || printing} onClick={printPdf}>{printing ? <Loader2 className="h-4 w-4 animate-spin"/> : <Printer className="h-4 w-4"/>}Print PDF</InkpadButton></div>
        </div>
        {!editable ? <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-semibold text-amber-950">{inkpadDocument ? 'Historical InkPad prescription: view and PDF remain available; editing requires active InkPad access.' : quotaExhausted ? <div><p>Your {access.freeAllowance} free Dektal InkPad prescriptions have been used.</p><a href="/addons" className="mt-2 inline-flex rounded-xl bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700">Unlock Dektal InkPad — {formatInkpadPrice(price) || 'configured price'}/month</a></div> : 'Dektal InkPad is currently unavailable for new prescriptions.'}</div> : null}
        <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-3 py-3 lg:grid-cols-2 sm:px-5">
          <section className="rounded-xl border border-slate-200 bg-white p-3" aria-label="Treatments Performed">
            <div className="flex items-center gap-2"><Plus className="h-4 w-4 text-indigo-600"/><h3 className="text-xs font-bold uppercase tracking-wide text-slate-800">Treatments Performed</h3></div>
            <p className="mt-1 text-[11px] text-slate-500">Only structured entries—not handwriting—flow to billing, inventory and Patient History.</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(120px,0.45fr)_auto]">
              <select aria-label="Select performed treatment" value={performedTreatmentId} onChange={(event) => setPerformedTreatmentId(event.target.value)} className="min-h-10 rounded-xl border border-slate-200 px-3 text-xs"><option value="">Select treatment</option>{treatmentCatalog.map((item) => <option key={`${item.sourceType || 'master'}-${item.id}`} value={item.id}>{item.name || item.treatmentName}</option>)}</select>
              <input aria-label="Performed treatment tooth or site" value={performedToothSite} onChange={(event) => setPerformedToothSite(event.target.value)} placeholder="Tooth / Site" className="min-h-10 rounded-xl border border-slate-200 px-3 text-xs" />
              <InkpadButton disabled={!editable || !performedTreatmentId || savingTreatment} onClick={addPerformedTreatment}>{savingTreatment ? <Loader2 className="h-4 w-4 animate-spin"/> : <Plus className="h-4 w-4"/>}Add Performed</InkpadButton>
            </div>
          </section>
          <section className="rounded-xl border border-slate-200 bg-white p-3" aria-label="Follow-up Next Visit">
            <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-teal-600"/><h3 className="text-xs font-bold uppercase tracking-wide text-slate-800">Follow-up / Next Visit</h3></div>
            <p className="mt-1 text-[11px] text-slate-500">Schedule explicitly; handwritten dates are never interpreted.</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]"><input aria-label="Next visit date" type="date" value={followUpDate} onChange={(event) => setFollowUpDate(event.target.value)} className="min-h-10 rounded-xl border border-slate-200 px-3 text-xs"/><input aria-label="Next visit time" type="time" value={followUpTime} onChange={(event) => setFollowUpTime(event.target.value)} className="min-h-10 rounded-xl border border-slate-200 px-3 text-xs"/><InkpadButton disabled={!editable || savingFollowUp || Boolean(lastFollowUp && lastFollowUp.dueDate === followUpDate)} onClick={scheduleFollowUp}>{savingFollowUp ? <Loader2 className="h-4 w-4 animate-spin"/> : <CalendarClock className="h-4 w-4"/>}Schedule</InkpadButton></div>
          </section>
        </div>
        <main className="min-h-0 flex-1 overflow-auto p-3 sm:p-6">
          <div className="relative mx-auto overflow-hidden bg-white shadow-[0_12px_45px_-20px_rgba(15,23,42,0.35)]" style={{ width: 'min(100%, 794px)', aspectRatio: `${INKPAD_PAGE_SIZE.width}/${INKPAD_PAGE_SIZE.height}` }}>
            {currentPage?.backgroundType === 'clinic' && backgroundUrl ? <img src={backgroundUrl} alt="Clinic prescription background" className="pointer-events-none absolute inset-0 z-0 h-full w-full object-contain"/> : null}
            {(currentPage?.strokes || []).filter(isImageItem).map((item) => <div key={item.id} role="button" tabIndex={0} aria-label={`Inserted clinical image ${item.attachmentId}`} onPointerDown={(event) => beginImageGesture(event, item, 'move')} onPointerMove={moveImageGesture} onPointerUp={endImageGesture} onPointerCancel={endImageGesture} className={`absolute z-10 touch-none ${selectedImageId === item.id ? 'ring-2 ring-indigo-500' : ''}`} style={{ left: `${item.x / INKPAD_PAGE_SIZE.width * 100}%`, top: `${item.y / INKPAD_PAGE_SIZE.height * 100}%`, width: `${item.width / INKPAD_PAGE_SIZE.width * 100}%`, height: `${item.height / INKPAD_PAGE_SIZE.height * 100}%`, pointerEvents: tool === 'select' ? 'auto' : 'none' }}><img src={imageUrls[item.attachmentId] || ''} alt="Inserted dental clinical reference" draggable="false" className="h-full w-full select-none object-fill"/>{selectedImageId === item.id && tool === 'select' ? <button type="button" aria-label="Resize selected image" onPointerDown={(event) => beginImageGesture(event, item, 'resize')} onPointerMove={moveImageGesture} onPointerUp={endImageGesture} onPointerCancel={endImageGesture} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-se-resize rounded-full border-2 border-white bg-indigo-600 shadow"/> : null}</div>)}
            <canvas ref={canvasRef} width={INKPAD_PAGE_SIZE.width} height={INKPAD_PAGE_SIZE.height} aria-label={`InkPad drawing page ${pageIndex + 1}`} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrawing} onPointerCancel={endDrawing} onLostPointerCapture={endDrawing} className={`absolute inset-0 z-20 h-full w-full ${editable ? (tool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair') : 'cursor-default'}`} style={{ touchAction: drawing ? 'none' : 'pan-y', pointerEvents: tool === 'select' ? 'none' : 'auto' }}/>
          </div>
        </main>
        <footer className="flex items-center justify-center gap-3 border-t border-slate-200 bg-white px-4 py-2"><InkpadButton disabled={pageIndex === 0} onClick={() => setPageIndex((value) => value - 1)}><ChevronLeft className="h-4 w-4"/>Previous</InkpadButton><span className="min-w-24 text-center text-xs font-bold text-slate-700">Page {pageIndex + 1} of {pages.length}{dirty ? ' · Unsaved' : ''}</span><InkpadButton disabled={pageIndex >= pages.length - 1} onClick={() => setPageIndex((value) => value + 1)}>Next<ChevronRight className="h-4 w-4"/></InkpadButton></footer>
      </>}
    </div>
  </div>;
}
